"use client";

import { memo, useCallback, useState } from "react";
import { Handle, NodeResizer, Position, useReactFlow } from "@xyflow/react";
import type { NodeProps } from "@xyflow/react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import {
  channelLabel,
  directionForFunnelLocale,
  formatFunnelNumber,
  funnelText,
  textAlignForFunnelLocale,
} from "@/lib/funnel-planner/i18n";
import {
  SOURCE_NODE_DEFAULT_HEIGHT,
  SOURCE_NODE_DEFAULT_WIDTH,
  SOURCE_NODE_MAX_HEIGHT,
  SOURCE_NODE_MAX_WIDTH,
  SOURCE_NODE_MIN_HEIGHT,
  SOURCE_NODE_MIN_WIDTH,
  clampNodeDimension,
  normalizeNodeColor,
} from "./node-visuals";

type TrafficSourceData = {
  label: string;
  channel?: string;
  monthlyVisitors?: number;
  volume?: number;
  color?: string;
  boxWidth?: number;
  boxHeight?: number;
  isSource?: boolean;
  content?: FunnelPlannerContent;
  locale?: AppLocale;
  onOpenMenu?: (id: string) => void;
};

const CHANNEL_ICONS: Record<string, string> = {
  google_organic: "G",  bing_organic: "B",  other_search: "S",
  google_ads: "G$",     bing_ads: "B$",     ppc_source: "P$",
  facebook_instagram: "f", instagram: "IG", linkedin: "in",  youtube: "YT",
  tiktok: "TT",         pinterest: "P",     facebook_ads: "f$",
  linkedin_ads: "in$",  youtube_ads: "YT$", email_newsletter: "EM",
  email_ads: "EM$",     blog_articles: "A", referral: "R",
  affiliates: "AF",     custom: "C",
  retargeting: "RTG",   direct_mail: "DM",  review_sites: "RV",
  radio_ad: "RA",        radio_ads: "RA",     podcast: "PO",
  sms: "SMS",            sms_marketing: "SMS",
};

const CHANNEL_COLORS: Record<string, string> = {
  google_organic: "#4285f4",
  bing_organic: "#0078d4",
  other_search: "#64748b",
  google_ads: "#ea4335",
  bing_ads: "#0078d4",
  ppc_source: "#f97316",
  facebook_instagram: "#1877f2",
  instagram: "#c13584",
  linkedin: "#0a66c2",
  youtube: "#ff0000",
  tiktok: "#111827",
  pinterest: "#e60023",
  facebook_ads: "#1877f2",
  linkedin_ads: "#0a66c2",
  youtube_ads: "#ff0000",
  email_newsletter: "#10b981",
  email_ads: "#0ea5e9",
  blog_articles: "#8b5cf6",
  referral: "#f59e0b",
  affiliates: "#06b6d4",
  retargeting: "#7c3aed",
  direct_mail: "#0369a1",
  review_sites: "#d97706",
  radio_ad: "#dc2626",
  radio_ads: "#dc2626",
  podcast: "#7c3aed",
  sms: "#0891b2",
  sms_marketing: "#0891b2",
  custom: "#2563eb",
};

function getChannelIcon(channel?: string): string {
  return CHANNEL_ICONS[channel ?? ""] ?? channel?.slice(0, 2).toUpperCase() ?? "?";
}

function getChannelColor(channel?: string): string {
  return CHANNEL_COLORS[channel ?? ""] ?? "#2563eb";
}

export const TrafficSourceNode = memo(function TrafficSourceNode({
  id,
  data,
  selected,
}: NodeProps) {
  const d = data as TrafficSourceData;
  const { updateNodeData } = useReactFlow();
  const visitors = d.monthlyVisitors ?? 500;
  const color = normalizeNodeColor(d.color) ?? getChannelColor(d.channel);
  const nodeWidth = clampNodeDimension(d.boxWidth, SOURCE_NODE_MIN_WIDTH, SOURCE_NODE_MAX_WIDTH, SOURCE_NODE_DEFAULT_WIDTH);
  const nodeHeight = clampNodeDimension(d.boxHeight, SOURCE_NODE_MIN_HEIGHT, SOURCE_NODE_MAX_HEIGHT, SOURCE_NODE_DEFAULT_HEIGHT);
  const [editingVisitors, setEditingVisitors] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const content = d.content;
  const locale = d.locale ?? "en";
  const nodeDirection = directionForFunnelLocale(locale);
  const nodeTextAlign = textAlignForFunnelLocale(locale);
  const t = (key: string, fallback: string, values?: Record<string, string | number>) =>
    content ? funnelText(content, key, fallback, values) : fallback;
  const channelDisplay = d.channel
    ? content
      ? channelLabel(content, d.channel, d.channel.replace(/_/g, " "))
      : d.channel.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : t("flowCustomStage", "Custom");

  const handleVisitorsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val >= 0) updateNodeData(id, { monthlyVisitors: val });
  }, [id, updateNodeData]);

  const handleResize = useCallback((_event: unknown, params: { width: number; height: number; direction?: number[] }) => {
    const shouldUpdateWidth = params.direction ? params.direction[0] !== 0 : true;
    const shouldUpdateHeight = params.direction ? params.direction[1] !== 0 : true;
    updateNodeData(id, {
      boxWidth: shouldUpdateWidth
        ? clampNodeDimension(params.width, SOURCE_NODE_MIN_WIDTH, SOURCE_NODE_MAX_WIDTH, SOURCE_NODE_DEFAULT_WIDTH)
        : nodeWidth,
      boxHeight: shouldUpdateHeight
        ? clampNodeDimension(params.height, SOURCE_NODE_MIN_HEIGHT, SOURCE_NODE_MAX_HEIGHT, SOURCE_NODE_DEFAULT_HEIGHT)
        : nodeHeight,
    });
  }, [id, nodeHeight, nodeWidth, updateNodeData]);

  const handleLabelBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value.trim();
    updateNodeData(id, { label: val || d.label });
    setEditingLabel(false);
  }, [id, d.label, updateNodeData]);

  const handleLabelKey = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
  }, []);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    d.onOpenMenu?.(id);
  }, [d, id]);

  return (
    <div
      dir={nodeDirection}
      data-funnel-planner-node-direction={nodeDirection}
      style={{
        width: nodeWidth,
        minHeight: nodeHeight,
        borderRadius: 10,
        border: selected ? `2px solid ${color}` : `1.5px solid ${color}66`,
        background: `linear-gradient(135deg, ${color} 0%, color-mix(in srgb, ${color} 58%, #020617) 100%)`,
        boxShadow: selected
          ? `0 0 0 3px ${color}40, 0 4px 16px rgba(0,0,0,0.25)`
          : "0 2px 8px rgba(0,0,0,0.18)",
        overflow: "visible",
        cursor: "default",
        userSelect: "none",
        position: "relative",
        direction: nodeDirection,
        textAlign: nodeTextAlign,
        "--funnel-planner-text-align": nodeTextAlign,
        "--funnel-planner-direction": nodeDirection,
      } as React.CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
    >
      {d.onOpenMenu && (
        <button
          type="button"
          onClick={handleOpenMenu}
          title={t("canvasTrafficSourceTitle", "Traffic Source")}
          aria-label={t("canvasTrafficSourceTitle", "Traffic Source")}
          className="nodrag nopan"
          style={{
            position: "absolute",
            top: 6,
            insetInlineStart: 6,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "rgba(15,23,42,0.78)",
            border: "1px solid rgba(255,255,255,0.4)",
            color: "#ffffff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            zIndex: 11,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M13.586 3.586a2 2 0 0 1 2.828 2.828l-.793.793-2.828-2.828.793-.793Z" />
            <path d="m11.379 5.793 2.828 2.828-7.5 7.5H3.879v-2.828l7.5-7.5Z" />
          </svg>
        </button>
      )}
      <NodeResizer
        isVisible={Boolean(selected)}
        minWidth={SOURCE_NODE_MIN_WIDTH}
        minHeight={SOURCE_NODE_MIN_HEIGHT}
        maxWidth={SOURCE_NODE_MAX_WIDTH}
        maxHeight={SOURCE_NODE_MAX_HEIGHT}
        color={color}
        onResize={handleResize}
        lineClassName="funnel-node-resize-line"
        handleClassName="funnel-node-resize-handle"
        handleStyle={{
          width: 9,
          height: 9,
          border: `1px solid ${color}`,
          background: "var(--color-canvas-bg, #0f172a)",
        }}
        lineStyle={{
          borderColor: color,
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px 6px" }}>
        <div
          title={t("canvasChannelTitle", "Channel: {channel}", { channel: channelDisplay })}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 10,
            fontWeight: 700,
            color: "white",
            flexShrink: 0,
            letterSpacing: 0,
            cursor: "help",
          }}
        >
          {getChannelIcon(d.channel)}
        </div>
        {editingLabel ? (
          <input
            autoFocus
            dir={nodeDirection}
            defaultValue={d.label}
            onBlur={handleLabelBlur}
            onKeyDown={handleLabelKey}
            style={{
              width: "100%",
              fontSize: 11,
              fontWeight: 600,
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: 4,
              color: "white",
              padding: "2px 5px",
              outline: "none",
              direction: nodeDirection,
              textAlign: nodeTextAlign,
            }}
          />
        ) : (
          <div
            onClick={() => setEditingLabel(true)}
            title={t("canvasClickToEditName", "Click to edit name")}
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "white",
              lineHeight: 1.3,
              overflow: "visible",
              textOverflow: "clip",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              cursor: "text",
              flex: 1,
              padding: "2px 3px",
              borderRadius: 3,
              border: "1px solid transparent",
              direction: nodeDirection,
              textAlign: nodeTextAlign,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.3)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "transparent"; }}
          >
            {d.label}
          </div>
        )}
      </div>

      <div style={{ padding: "0 12px 10px" }}>
        {editingVisitors ? (
          <input
            autoFocus
            type="number"
            min={0}
            defaultValue={visitors}
            onBlur={e => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= 0) updateNodeData(id, { monthlyVisitors: val });
              setEditingVisitors(false);
            }}
            onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur(); }}
            onChange={handleVisitorsChange}
            style={{
              width: 90,
              fontSize: 10,
              fontWeight: 700,
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: 4,
              color: "white",
              padding: "2px 7px",
              outline: "none",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        ) : (
          <div
            onClick={() => setEditingVisitors(true)}
            title={t("canvasClickToEditVisitors", "Click to edit visitors/mo")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              background: "rgba(255,255,255,0.18)",
              borderRadius: 4,
              padding: "2px 7px",
              fontSize: 10,
              fontWeight: 700,
              color: "white",
              fontVariantNumeric: "tabular-nums",
              cursor: "text",
              border: "1px solid transparent",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.4)"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "transparent"; }}
          >
            {formatFunnelNumber(visitors, locale)}
            <span title={t("canvasMonthlyVisitorsTitle", "Monthly visitors from this channel")} style={{ fontWeight: 400, opacity: 0.7, marginInlineStart: 2, cursor: "help" }}>
              {t("perMonthLabel", "/mo")}
            </span>
          </div>
        )}
      </div>

      {/* Output handle — right side */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          width: 10,
          height: 10,
          background: "#93c5fd",
          border: "2px solid #1d4ed8",
          borderRadius: "50%",
          right: -6,
        }}
      />
    </div>
  );
});
